import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useToolDefinitions, useWorkflows } from '@/hooks';
import { usePermissions } from '@/hooks/use-permissions';
import { PreconditionsSection } from '@/modules/integrations/components/preconditions-section';
import { UsedInProtocolsSidebar } from '@/modules/integrations/components/used-in-protocols-sidebar';
import { ToolTestSection } from '@/modules/integrations/tool-test/tool-test-section';

const TABS = [
	{ value: 'preconditions', label: 'Preconditions' },
	{ value: 'test', label: 'Test' }
] as const;
type Tab = (typeof TABS)[number]['value'];

// Same underline tabs as the Integrations settings header.
const TAB_TRIGGER = cn(
	'h-10 flex-none gap-1.5 px-0 py-[11px] text-[13px] font-normal leading-[18px] bg-transparent rounded-none border-0 shadow-none text-gray-800 transition-colors',
	'data-[state=active]:text-ink-strong data-[state=active]:font-medium data-[state=active]:border-b-2 data-[state=active]:border-ink-strong data-[state=active]:drop-shadow-none'
);

/** One action's detail: its preconditions and a test run in tabs, with the protocols using it in a side panel and a link to its calls in Logs. */
export default function IntegrationActionDetailPage() {
	const { provider, tool } = useParams<{ provider: string; tool: string }>();
	const navigate = useNavigate();
	// A page that sends the admin here can open it on a tab.
	const location = useLocation();
	const requestedTab = (location.state as { tab?: string } | null)?.tab;
	// For this visit only: coming back to this entry later opens on preconditions, not the tab asked for then.
	useEffect(() => {
		if (requestedTab) navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
	}, [requestedTab, location.pathname, location.search, navigate]);

	const { data: tools, isLoading: isActionLoading } = useToolDefinitions();
	const action = (tools ?? []).find((t) => t.provider.toLowerCase() === provider?.toLowerCase() && t.tool === tool);

	const { data: usage, isLoading: isUsageLoading } = useWorkflows({ tool: tool ?? '', provider: provider ?? '' });
	const protocols = usage?.workflows ?? [];
	const [panel, setPanel] = useState<'protocols' | null>(null);
	// The old 'events' tab moved to Settings › Logs; it and any unknown tab open on preconditions.
	const [tab, setTab] = useState<Tab>(TABS.find((t) => t.value === requestedTab)?.value ?? 'preconditions');
	const { can } = usePermissions();
	const testableAction = action?.testable && can('edit', 'integrations') ? action : null;
	const tabs = TABS.filter((t) => t.value !== 'test' || testableAction);
	const activeTab = tab === 'test' && !testableAction ? 'preconditions' : tab;
	const panelRef = useRef<HTMLDivElement>(null);
	const protocolsToggleRef = useRef<HTMLButtonElement>(null);

	// Close the panel on a click anywhere outside it; the protocols toggle opens and closes it itself.
	useEffect(() => {
		if (!panel) return;
		const handleOutsideClick = (event: MouseEvent) => {
			const target = event.target as Node;
			if (panelRef.current?.contains(target) || protocolsToggleRef.current?.contains(target)) return;
			setPanel(null);
		};
		document.addEventListener('mousedown', handleOutsideClick);
		return () => document.removeEventListener('mousedown', handleOutsideClick);
	}, [panel]);

	return (
		<div className='relative flex h-full'>
			<div className='flex flex-1 min-w-0 flex-col h-full'>
				<div className='sticky top-0 z-10 bg-white flex shrink-0 flex-col gap-1 px-4 pt-3 border-b border-gray-500'>
					<div className='flex items-center gap-2'>
						<Button
							variant='ghost'
							size='icon'
							className='size-7 rounded-full shrink-0'
							aria-label='Back'
							onClick={() => navigate(`/settings/integrations/${provider}/actions`)}
						>
							<ChevronLeft size={16} />
						</Button>
						<div className='flex min-w-0 flex-col'>
							<h1 className='text-base font-semibold'>{isActionLoading ? tool : (action?.label ?? tool)}</h1>
							<div className='flex items-center gap-2 text-[13px] text-muted-foreground'>
								{action?.description && <p>{action.description}</p>}
								{/* Only when the action is used somewhere; an empty list isn't worth a link. */}
								{protocols.length > 0 && (
									<>
										{action?.description && <span aria-hidden className='h-3 w-px shrink-0 bg-gray-500' />}
										<button
											ref={protocolsToggleRef}
											type='button'
											className='shrink-0 text-indigo-500 hover:underline'
											aria-expanded={panel === 'protocols'}
											onClick={() => setPanel(panel === 'protocols' ? null : 'protocols')}
										>
											{`Used in ${protocols.length} protocol${protocols.length === 1 ? '' : 's'}`}
										</button>
									</>
								)}
								{/* Settings › Logs is gated on automation; without it the link would bounce home. */}
								{can('view', 'integrations') && can('view', 'automation') && (
									<>
										{(action?.description || protocols.length > 0) && <span aria-hidden className='h-3 w-px shrink-0 bg-gray-500' />}
										<Link
											to={`/settings/logs?${new URLSearchParams({ tab: 'tool-server', provider: provider ?? '', tool: tool ?? '' })}`}
											className='shrink-0 text-indigo-500 hover:underline'
										>
											View logs
										</Link>
									</>
								)}
							</div>
						</div>
					</div>
					<Tabs value={activeTab} onValueChange={(value) => setTab(value as Tab)}>
						<TabsList className='flex gap-5 bg-transparent p-0 h-auto border-0 rounded-none justify-start'>
							{tabs.map((t) => (
								<TabsTrigger key={t.value} value={t.value} className={TAB_TRIGGER}>
									{t.label}
								</TabsTrigger>
							))}
						</TabsList>
					</Tabs>
				</div>

				{/* Only this area scrolls; `relative` also clips absolute children (sr-only labels) that would scroll the page. */}
				<div className='relative flex flex-1 min-h-0 flex-col p-6 overflow-auto'>
					{activeTab === 'preconditions' && <PreconditionsSection provider={provider ?? ''} tool={tool ?? ''} />}
					{activeTab === 'test' && testableAction && (
						<ToolTestSection key={`${testableAction.provider}:${testableAction.tool}`} action={testableAction} />
					)}
				</div>
			</div>
			{panel && (
				// Slides over the page, like the workflow node drawer, instead of pushing the content aside.
				<div
					ref={panelRef}
					className='absolute inset-y-0 right-0 z-20 flex shadow-[-16px_0_40px_-16px_rgba(16,24,40,0.22)] animate-in slide-in-from-right duration-200'
				>
					<UsedInProtocolsSidebar protocols={protocols} isLoading={isUsageLoading} onClose={() => setPanel(null)} />
				</div>
			)}
		</div>
	);
}
